import { gql } from '@apollo/client';
import PluginEditor from '@draft-js-plugins/editor';
import { useRouter } from 'next/router';
import * as React from 'react';
import yn from 'yn';

import {
  useCommentActions_PostFragment,
  useCommentActions_PostFragment_comments,
} from './graphql-types/useCommentActions_PostFragment';

import {
  COMMENT_QUERY_PARAMETER_NAME,
  CREATE_COMMENT_NAME,
  REPLY_TO_COMMENT_NAME,
} from '../components/Comments/utils';
import { IExpandProps, useExpand } from './useExpand';

const COMMENT_COUNT_EXPAND_INCREMENT = 5;

const getIndexOfFirstComment = (options: {
  comments: useCommentActions_PostFragment_comments[];
  commentIdToShow?: string | null;
}): number => {
  const { comments, commentIdToShow } = options;
  if (!commentIdToShow) {
    return -1;
  }

  const foundIndex = comments.reduce((foundIndex, comment, index) => {
    if (foundIndex !== -1) {
      return foundIndex;
    }

    if (
      comment._id === commentIdToShow ||
      comment.comments.find(
        (childComment) => childComment._id === commentIdToShow
      )
    ) {
      return index;
    }
    return -1;
  }, -1);

  return foundIndex;
};

export const useCommentActions = (options: {
  post: useCommentActions_PostFragment;
  isExpandedByDefault?: boolean;
}): {
  ref: React.RefObject<PluginEditor>;
  focusAndScrollToRef: () => void;
  expandOrFocusAndScrollToRef: () => void;
  isActive: boolean;
  setIsActive: React.Dispatch<React.SetStateAction<boolean>>;
} & IExpandProps => {
  const router = useRouter();

  const queryParamsCommentId =
    router.query[COMMENT_QUERY_PARAMETER_NAME]?.toString();
  const replyToComment = router.query[REPLY_TO_COMMENT_NAME]?.toString();

  const startingCommentIndex = getIndexOfFirstComment({
    comments: options.post.comments.slice(0).reverse(),
    commentIdToShow: queryParamsCommentId || replyToComment,
  });

  const ref = React.useRef<PluginEditor>(null);

  const intialNumShown =
    startingCommentIndex !== -1 ? startingCommentIndex + 1 : 1;

  const expandProps = useExpand({
    items: options.post.comments,
    initialNumShown: options.isExpandedByDefault
      ? options.post.comments.length
      : intialNumShown,
  });

  const [isActive, setIsActive] = React.useState(false);

  React.useEffect(() => {
    if (router.isReady) {
      setIsActive(
        yn(router.query[CREATE_COMMENT_NAME]?.toString(), { default: false })
      );
    }
  }, [router]);

  return {
    ref,
    focusAndScrollToRef: () => {
      setIsActive(true);
      ref.current?.focus();
      // @ts-ignore
      ref.current?.editor?.editor?.parentElement?.scrollIntoView?.({
        behavior: 'smooth',
        block: 'center',
      });
    },
    expandOrFocusAndScrollToRef: () => {
      if (options.post.comments.length > 0 && expandProps.numHidden > 0) {
        expandProps.expand(COMMENT_COUNT_EXPAND_INCREMENT);
      } else {
        setIsActive(true);
        ref.current?.focus();
        // @ts-ignore
        ref.current?.editor?.editor?.parentElement?.scrollIntoView?.({
          behavior: 'smooth',
          block: 'center',
        });
      }
    },
    isActive,
    setIsActive,
    ...expandProps,
  };
};

useCommentActions.postFragment = gql`
  fragment useCommentActions_PostFragment on PostV2 {
    _id
    comments {
      _id
      comments {
        _id
      }
    }
  }
`;
